/**
 * Input-mode policy: how the compiler filters and groups raw input for playback.
 * A raw package records the policy it was captured with; source-graph nodes may override
 * everything except `captureSource`, which is fixed per recording.
 */
import { z } from 'zod';
import { macroInputDeviceSettingsSchema } from '../macroInputDevices';

const filterSwitch = z.boolean().default(false);
const gamepadEventFilterSchema = z.object({
  a: filterSwitch, b: filterSwitch, x: filterSwitch, y: filterSwitch,
  leftShoulder: filterSwitch, rightShoulder: filterSwitch, back: filterSwitch, start: filterSwitch,
  leftStickButton: filterSwitch, rightStickButton: filterSwitch,
  dpadUp: filterSwitch, dpadDown: filterSwitch, dpadLeft: filterSwitch, dpadRight: filterSwitch,
  leftTrigger: filterSwitch, rightTrigger: filterSwitch, leftStick: filterSwitch, rightStick: filterSwitch,
}).strict().default({});
const eventFiltersSchema = z.object({
  mouse: z.object({ motion: filterSwitch, wheel: filterSwitch,
    left: filterSwitch, right: filterSwitch, middle: filterSwitch, back: filterSwitch, forward: filterSwitch }).strict().default({}),
  keyboard: filterSwitch, touch: filterSwitch,
  gamepad: z.object({ gamepad0: gamepadEventFilterSchema, gamepad1: gamepadEventFilterSchema,
    gamepad2: gamepadEventFilterSchema, gamepad3: gamepadEventFilterSchema }).strict().default({}),
}).strict().default({});

export const mouseMotionModeSchema = z.enum(['relative', 'absolute-clicks', 'absolute-path']);
export type MouseMotionMode = z.infer<typeof mouseMotionModeSchema>;

/** Runtime mapping from recorded coordinates onto the selected playback target. */
export const macroInputCoordinatePolicySchema = z.object({
  aspect: z.enum(['stretch', 'fit']).default('stretch'),
  relativeMouse: z.enum(['raw', 'scaled']).default('raw'),
}).strict();
export type MacroInputCoordinatePolicy = z.infer<typeof macroInputCoordinatePolicySchema>;
export const DEFAULT_INPUT_COORDINATE_POLICY: MacroInputCoordinatePolicy = { aspect: 'stretch', relativeMouse: 'raw' };

const policyBody = {
  captureSource: z.enum(['desktop', 'editor']),
  devices: macroInputDeviceSettingsSchema,
  /** Per-event compile filters. `true` means the category is omitted from compiled input. */
  eventFilters: eventFiltersSchema,
  /** Absent preserves legacy package bytes and means stretch/raw at playback. */
  coordinates: macroInputCoordinatePolicySchema.optional(),
  mouse: z.object({ motion: mouseMotionModeSchema,
    relativeMaxHz: z.number().int().min(1).max(1000), absolutePathMaxHz: z.number().int().min(1).max(240),
    /** In 0..65535 normalized units. */
    absolutePathMinDelta: z.number().int().min(0).max(4096),
    drag: z.object({ enabled: z.boolean(), minDistancePx: z.number().int().min(0).max(64),
      waypointIntervalMs: z.number().int().min(8).max(500) }).strict(),
    positionClicks: z.boolean() }).strict(),
  keyboard: z.object({ collapseAutoRepeat: z.boolean(), minHoldMs: z.number().int().min(0).max(500) }).strict(),
  touch: z.object({ moveMaxHz: z.number().int().min(1).max(240), minMoveDelta: z.number().int().min(0).max(4096) }).strict(),
  gamepad: z.object({ deadzone: z.number().int().min(0).max(16_384), analogMaxHz: z.number().int().min(1).max(500),
    analogQuantize: z.number().int().min(1).max(4096) }).strict(),
  grouping: z.object({ chordWindowMs: z.number().int().min(0).max(200), holdThresholdMs: z.number().int().min(0).max(2000),
    multitouchWindowMs: z.number().int().min(0).max(200) }).strict(),
  timing: z.object({ mode: z.enum(['recorded', 'compressed']), maxIdleGapMs: z.number().int().min(0).max(600_000) }).strict(),
};

export const macroInputModePolicySchema = z.object({ version: z.literal(1), ...policyBody }).strict();
export type MacroInputModePolicy = z.infer<typeof macroInputModePolicySchema>;

export const macroInputModePolicyOverrideSchema = z.object(policyBody).deepPartial().strict()
  .refine(override => override.captureSource === undefined, 'captureSource is per recording');
export type MacroInputModePolicyOverride = z.infer<typeof macroInputModePolicyOverrideSchema>;

export const ALL_DEVICES_ENABLED = macroInputDeviceSettingsSchema.parse({});

export const DEFAULT_INPUT_MODE_POLICY: MacroInputModePolicy = { version: 1, captureSource: 'desktop',
  devices: ALL_DEVICES_ENABLED, eventFilters: eventFiltersSchema.parse({}), mouse: { motion: 'absolute-clicks', relativeMaxHz: 250, absolutePathMaxHz: 60,
    absolutePathMinDelta: 16, drag: { enabled: true, minDistancePx: 5, waypointIntervalMs: 60 }, positionClicks: true },
  keyboard: { collapseAutoRepeat: true, minHoldMs: 20 }, touch: { moveMaxHz: 120, minMoveDelta: 8 },
  gamepad: { deadzone: 1_600, analogMaxHz: 120, analogQuantize: 64 },
  grouping: { chordWindowMs: 30, holdThresholdMs: 200, multitouchWindowMs: 20 },
  timing: { mode: 'recorded', maxIdleGapMs: 600_000 } };

/** Converts a complete capture policy into the editable fields accepted by source-graph nodes. */
export function policyOverrideFromPolicy(policy: MacroInputModePolicy): MacroInputModePolicyOverride {
  return { devices: policy.devices, eventFilters: policy.eventFilters, mouse: policy.mouse, keyboard: policy.keyboard, touch: policy.touch,
    gamepad: policy.gamepad, grouping: policy.grouping, timing: policy.timing,
    ...(policy.coordinates ? { coordinates: policy.coordinates } : {}) };
}

/**
 * Deep-merges overrides onto `base` (later overrides win) and re-validates the result.
 * Throws when an override sets `captureSource` or produces an invalid policy.
 */
export function resolvePolicy(base: MacroInputModePolicy, ...overrides: MacroInputModePolicyOverride[]): MacroInputModePolicy {
  let merged: unknown = base;
  for (const override of overrides) {
    macroInputModePolicyOverrideSchema.parse(override);
    merged = deepMerge(merged, override);
  }
  return macroInputModePolicySchema.parse(merged);
}

function deepMerge(target: unknown, patch: unknown): unknown {
  if (!isPlainObject(target) || !isPlainObject(patch)) return patch === undefined ? target : patch;
  const result: Record<string, unknown> = { ...target };
  for (const [key, value] of Object.entries(patch)) {
    if (value !== undefined) result[key] = deepMerge(target[key], value);
  }
  return result;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
